import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Music4, ArrowLeft } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      navigate(homeFor(user.role));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-16 lg:px-24 py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-10 transition-colors" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center">
            <Music4 className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <p className="font-display font-bold leading-none">Cornerstone</p>
            <p className="text-[9px] uppercase tracking-[0.25em] text-secondary font-bold">Music School</p>
          </div>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Portal sign in</h1>
        <p className="text-sm text-muted-foreground mb-8">
          Admin, finance, teachers and parents — one login for everything.
        </p>
        <form onSubmit={submit} className="space-y-5 max-w-sm" data-testid="login-form">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</Label>
            <Input id="email" type="email" data-testid="login-email-input" value={email}
              placeholder="you@cornerstone.my" onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Password</Label>
            <Input id="password" type="password" data-testid="login-password-input" value={password}
              placeholder="••••••••" onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && (
            <p className="text-sm text-destructive bg-[#FFEDEC] rounded-md px-3 py-2 whitespace-pre-line" data-testid="login-error">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
            {busy ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        <div className="max-w-sm mt-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={googleSignIn} data-testid="google-signin-button">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.94l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Continue with Google
          </Button>
          <p className="text-[11px] text-muted-foreground mt-2">
            New here? Google sign-in creates a parent account instantly — no password needed.
          </p>
        </div>
        <div className="mt-10 max-w-sm rounded-lg border bg-card p-4 text-xs text-muted-foreground space-y-2" data-testid="demo-credentials">
          <p className="font-bold text-foreground uppercase tracking-wider text-[10px] mb-1">Try a demo account</p>
          <div>
            <p className="font-semibold text-foreground">Admin (full access)</p>
            <p>admin@cornerstone.my / Admin@123</p>
          </div>
          <div>
            <p className="font-semibold text-foreground">Finance</p>
            <p>finance@cornerstone.my / Finance@123</p>
          </div>
          <div>
            <p className="font-semibold text-foreground">Teachers</p>
            <p>sweekoon82+sarah@gmail.com / Teacher@123 (Sarah Tan)</p>
            <p>sweekoon82+david@gmail.com / Teacher@123 (David Lim)</p>
          </div>
          <div>
            <p className="font-semibold text-foreground">Parents</p>
            <p>sweekoon82+daniel@gmail.com / Parent@123 (children: Emma & Ethan Wong)</p>
            <p>sweekoon82+raj@gmail.com / Parent@123 (child: Aisha Kumar)</p>
          </div>
        </div>
      </div>
      <div className="hidden lg:flex relative stage-dark flex-col justify-end overflow-hidden">
        <div className="absolute inset-x-0 bottom-0 h-20 piano-keys opacity-60 pointer-events-none" aria-hidden="true" />
        <div className="relative p-12 pb-28 text-white">
          <div className="soundwave mb-8" aria-hidden="true">
            {Array.from({ length: 22 }).map((_, i) => (
              <span key={i} style={{ height: `${16 + ((i * 11) % 34)}px`, animationDelay: `${(i % 6) * 0.2}s` }} />
            ))}
          </div>
          <p className="font-display text-3xl font-semibold leading-tight max-w-md">
            Every lesson, every invoice, every student — <span className="gold-text italic">in one place.</span>
          </p>
          <p className="text-sm text-white/60 mt-3">Cornerstone Music School Management System</p>
        </div>
      </div>
    </div>
  );
}
